import {
  Banner,
  Container,
  Header,
  Layout,
  MainContent,
  Section,
} from '@inmotionnow/momentum-components-react'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import { localeIdToValue, userTypeIdToValue } from '@/features/users/user-utils'
import { useCreateUserMutation } from '@/features/users/userApi'
import {
  UserCreateForm,
  UserCreateFormValues,
} from '@/features/users/UserCreateForm'
import {
  getBackendErrorMessage,
  isEmailConflictError,
  isSeatLimitError,
} from '@/features/users/userErrors'
import { logger } from '@/utils/logger'

import { BulkImportPanel } from './bulk-import-panel'
import { CreateUserTab, CreateUserTabs } from './create-user-tabs'

export type IUserCreateComponentProps = {}

/**
 * Standalone (GIM UI) Create User view. Wraps the shared UserCreateForm in the
 * app's page chrome (header + banner), collects the target account via the form's
 * Account ID field, submits through the staff endpoint, and navigates to the new
 * user's detail page on success.
 */
const UserCreateView = (_props: IUserCreateComponentProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [createUser, { isLoading: isCreating }] = useCreateUserMutation()
  const [emailError, setEmailError] = React.useState<string | undefined>(
    undefined,
  )

  const handleSubmit = async (values: UserCreateFormValues): Promise<void> => {
    const userType = userTypeIdToValue(values.userTypeId!)
    if (!userType) return

    const accountId = parseInt(values.accountId, 10)

    try {
      const result = await createUser({
        account_id: accountId,
        user: {
          email: values.email.trim(),
          first_name: values.firstName.trim(),
          last_name: values.lastName.trim(),
          title: values.title.trim() || undefined,
          phone: values.phone.trim() || undefined,
          locale: localeIdToValue(values.locale),
          timezone:
            values.timezone.trim() ||
            Intl.DateTimeFormat().resolvedOptions().timeZone,
          user_type: userType,
        },
      }).unwrap()

      toast.success(t('USER_CREATED_SUCCESSFULLY'))
      // Pass the account context so Cancel on the detail page returns to this
      // account, matching UserPublicCreateView.
      navigate(`/user/details/${result.id}?accountId=${accountId}`)
    } catch (error) {
      if (isEmailConflictError(error)) {
        setEmailError(t('EMAIL_ALREADY_IN_USE'))
      } else if (isSeatLimitError(error)) {
        toast.error(t('NO_TEAM_MEMBER_SEATS_AVAILABLE'))
      } else {
        // Prefer the backend's own explanation of a 4xx over the generic string,
        // so the user learns why the create was rejected (KPS-513).
        toast.error(getBackendErrorMessage(error) ?? t('ERROR_CREATING_USER'))
      }
      logger.error('Error creating user:', error)
    }
  }

  // Staff have no account of their own, so both tabs ask them to name one. The route
  // is already behind StaffGuard, which is what admits bulk import here — the backend
  // gate on the upload endpoint admits staff for the same reason (KPS-492 Decision 4).
  const tabs: CreateUserTab[] = [
    {
      id: 'staff-create-user-invite',
      label: t('INVITE'),
      panel: (
        <UserCreateForm
          showAccountIdField
          isSubmitting={isCreating}
          emailError={emailError}
          onEmailChange={() => setEmailError(undefined)}
          onSubmit={handleSubmit}
          onCancel={() => navigate(-1)}
        />
      ),
    },
    {
      id: 'staff-create-user-import',
      label: t('IMPORT'),
      panel: <BulkImportPanel />,
    },
  ]

  return (
    <Layout>
      <Header>
        <Banner title={t('CREATE_USER')} />
      </Header>

      <MainContent>
        <Section>
          <Container size='sm'>
            <CreateUserTabs tabs={tabs} ariaLabel={t('CREATE_USER')} />
          </Container>
        </Section>
      </MainContent>
    </Layout>
  )
}

export { UserCreateView }
